import { useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

const TOOL_INFO_CONTENT = {
  what: `Reverses text character by character, or removes duplicate lines or duplicate words from a list — related but distinct text-manipulation utilities in one tool.`,
  how: `Paste your text and choose whether you want it reversed, de-duplicated (by line or by word), or sorted, then copy the result. Use Clear to start over with different text.`,
  tip: `Remove Duplicate Lines and Sort Lines both work on whole lines specifically — if your text doesn't have any repeated lines, or is already in order, the result will look identical to the input, which is correct, not a sign it didn't run (a confirmation message now shows exactly what happened either way). "Ignore case" and "Trim spaces" control how duplicates are matched for both the line and word removers.`,
};

// Splits into graphemes, not UTF-16 code units -- split("") breaks emoji,
// skin-tone modifiers, flags, and combining accents by separating a
// character's surrogate pairs or combining marks from each other.
function graphemes(t: string): string[] {
  return Array.from(new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(t), (s) => s.segment);
}

function reverseText(t: string): string {
  return graphemes(t).reverse().join("");
}

function reverseEachWord(t: string): string {
  // Reverses the letters inside each word but keeps word order and all spacing/line breaks.
  return t.split(/(\s+)/).map((part) => (/^\s+$/.test(part) ? part : graphemes(part).reverse().join(""))).join("");
}

function reverseLines(t: string): string {
  // Strip exactly one trailing newline before reversing and re-add it
  // after, so "a\nb\nc\n" reverses to "c\nb\na\n" instead of turning the
  // trailing newline into a new leading blank line ("\nc\nb\na").
  const hadTrailingNewline = t.endsWith("\n");
  const body = hadTrailingNewline ? t.slice(0, -1) : t;
  const reversed = body.split("\n").reverse().join("\n");
  return hadTrailingNewline ? reversed + "\n" : reversed;
}

function reverseWords(t: string): string {
  // Reverses word order within each line, preserving line breaks --
  // splitting on a single space only (the original behavior) glued
  // words across newlines together and produced an order that was
  // neither "per line" nor "whole text", which read as broken rather
  // than any one coherent behavior.
  return t
    .split("\n")
    .map((line) => line.split(/\s+/).filter(Boolean).reverse().join(" "))
    .join("\n");
}

export default function TextReverser() {
  const { gateShare } = useShareGate();
  const [text, setText] = useState("");
  const [output, setOutput] = useState("");
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [trimSpaces, setTrimSpaces] = useState(true);
  const { logUsageNow } = useLogUsageOnAction();

  // normalize() is only for *matching* duplicates -- the kept copy is
  // always the original, untouched line/word, so trimming or
  // case-folding for comparison never changes what actually appears in
  // the output.
  const normalize = (s: string) => {
    let v = trimSpaces ? s.trim() : s;
    if (ignoreCase) v = v.toLowerCase();
    return v;
  };

  // Was a hardcoded/semi-hardcoded useLogToolUsage call -- a 1200ms-
  // debounced log that fires well after the button's own click handler
  // has already set and displayed the output. Confirmed as a real bug:
  // at the daily limit, the paywall opened but the reversed/sorted/
  // deduped text was already visible behind it. logUsageNow() is the
  // same gating pattern Merge PDF already uses correctly for its own
  // discrete action -- await it first, don't run the action if it
  // returns false.
  const runGuarded = async (fn: () => void) => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    fn();
  };

  const removeDuplicateLines = () => runGuarded(() => {
    const lines = text.split("\n");
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const line of lines) {
      const key = normalize(line);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(line);
      }
    }
    setOutput(unique.join("\n"));
    const removed = lines.length - unique.length;
    toast.success(removed > 0 ? `Removed ${removed} duplicate ${removed === 1 ? "line" : "lines"}, ${unique.length} left` : "No duplicate lines found — every line was already unique");
  });

  const removeDuplicateWords = () => runGuarded(() => {
    const words = text.split(/\s+/).filter(Boolean);
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const w of words) {
      const key = normalize(w);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(w);
      }
    }
    setOutput(unique.join(" "));
    const removed = words.length - unique.length;
    toast.success(removed > 0 ? `Removed ${removed} duplicate ${removed === 1 ? "word" : "words"}, ${unique.length} left` : "No duplicate words found — every word was already unique");
  });

  const sortLines = (sortDescending: boolean) => runGuarded(() => {
    const lines = text.split("\n");
    // Natural sort (numeric-aware, case-insensitive) instead of default
    // plain-text order, which put "10" before "2" and treated "Apple"
    // and "apple" as different sort keys.
    const sorted = [...lines].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
    if (sortDescending) sorted.reverse();
    setOutput(sorted.join("\n"));
    const alreadySorted = lines.join("\n") === sorted.join("\n");
    toast.success(alreadySorted ? "Lines were already in sorted order" : `Lines sorted ${sortDescending ? "Z to A" : "A to Z"}`);
  });

  const clearAll = () => {
    setText("");
    setOutput("");
  };

  return (
    <ToolContainer>
      <Textarea aria-label="Text to reverse, sort or de-duplicate" placeholder="Enter text..." value={text} onChange={e => setText(e.target.value)} className="min-h-[120px]" />
      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={() => runGuarded(() => { setOutput(reverseText(text)); toast.success("Text reversed"); })}>Reverse Text</Button>
        <Button variant="outline" size="sm" onClick={() => runGuarded(() => { setOutput(reverseEachWord(text)); toast.success("Each word reversed in place"); })}>Reverse Each Word</Button>
        <Button variant="outline" size="sm" onClick={() => runGuarded(() => { setOutput(reverseLines(text)); toast.success("Line order reversed"); })}>Reverse Lines</Button>
        <Button variant="outline" size="sm" onClick={() => runGuarded(() => { setOutput(reverseWords(text)); toast.success("Word order reversed (within each line)"); })}>Reverse Words</Button>
        <Button variant="outline" size="sm" onClick={removeDuplicateLines}>Remove Duplicate Lines</Button>
        <Button variant="outline" size="sm" onClick={removeDuplicateWords}>Remove Duplicate Words</Button>
        <Button variant="outline" size="sm" onClick={() => sortLines(false)}>Sort A–Z</Button>
        <Button variant="outline" size="sm" onClick={() => sortLines(true)}>Sort Z–A</Button>
        <Button variant="ghost" size="sm" onClick={clearAll}>Clear</Button>
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <label className="flex items-center gap-2"><Checkbox id="tr-ignore-case" aria-label="Ignore case (for duplicates)" checked={ignoreCase} onCheckedChange={(v) => setIgnoreCase(v === true)} /> Ignore case (for duplicates)</label>
        <label className="flex items-center gap-2"><Checkbox id="tr-trim" aria-label="Trim spaces (for duplicates)" checked={trimSpaces} onCheckedChange={(v) => setTrimSpaces(v === true)} /> Trim spaces (for duplicates)</label>
      </div>
      {output && (
        <div className="mt-4">
          <Textarea aria-label="Result" value={output} readOnly className="min-h-[120px]" />
          <Button size="sm" className="mt-2" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
